/**
 * Design playground: compile-time flag and shared React Query policy.
 * Side-effect free; safe to import from production route modules.
 */

/**
 * True when the dev server or build sets `VITE_PLAYGROUND_MODE=true` (e.g. `pnpm playground`).
 */
export function isPlaygroundMode(): boolean {
    return import.meta.env.VITE_PLAYGROUND_MODE === 'true'
}

/**
 * Portal Clerk bypass (unsigned portal shell) is only allowed in local-trust contexts so a mis-built
 * playground bundle cannot skip Clerk on a real deployment host. Requires `isPlaygroundMode()` plus one of:
 * Vite dev server, localhost / loopback hostname, or explicit `VITE_ALLOW_PORTAL_CLERK_BYPASS=true`.
 */
export function isPortalPlaygroundClerkBypassAllowed(): boolean {
    if (!isPlaygroundMode()) return false
    if (import.meta.env.DEV) return true
    if (import.meta.env.VITE_ALLOW_PORTAL_CLERK_BYPASS === 'true') return true
    if (typeof window === 'undefined') return false
    const h = window.location.hostname.toLowerCase()
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]'
}

/**
 * Origination / partner-config style queries: production retries 3×; playground avoids
 * refetch against a non-intercepted API origin and suppresses error boundary trips.
 */
export function playgroundAwareQueryRetryPolicy(isPlayground: boolean): {
    retry: 3 | false
    refetchOnMount: false | undefined
    throwOnError: false | undefined
} {
    if (isPlayground) {
        return { retry: false, refetchOnMount: false, throwOnError: false }
    }
    return { retry: 3, refetchOnMount: undefined, throwOnError: undefined }
}
